import axiosInstance from '../../api/axios.js';
import { formatControlSourceLabel, getControlEffectiveStatus } from '../../features/controls/functional.ts';
import type { ControlsListParams } from '../../queries/controls/keys.ts';

export type ControlListRow = {
  id: string;
  displayId: string;
  name: string;
  owner: string;
  lastEdited: string;
  frequency: string;
  type: string;
  soxProcess: string;
  significance: string;
  classification: 'business' | 'itgc';
  testExecutionCount: number;
  riskCount: number;
  /** API lifecycle status (draft/active). */
  apiStatus: string;
  /** Display status aligned with detail page (`getControlEffectiveStatus`). */
  effectiveStatus: 'draft' | 'active';
  importSource: string;
  /** Raw record fields for job badge matching. */
  controlId: string;
  clientControlId: string;
  controlWording: string;
  objective: string;
  controlNature: string;
  mrcControl: boolean | null;
  department: string;
  controlOwner: string;
  keyReportsLabel: string;
  externalAuditorReliance: boolean | null;
  assertions: string[];
  source: string;
  sourceLabel: string;
  /** ITGC only; empty/mdash display for business. */
  domainLabel: string;
  systemsLabel: string;
  /** True for seeded ITGC master controls (not deletable). */
  isSeeded: boolean;
};

export type ControlsListPageResult = {
  rows: ControlListRow[];
  totalItems: number;
  totalPages: number;
  /** False when server omitted `pagination.total_items` (count may be a lower bound). */
  hasExactTotal: boolean;
};

function formatDateCell(value: unknown): string {
  if (!value) return '';
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
}

function formatNatureLabel(value: string): string {
  const raw = value.trim();
  if (!raw) return '';
  const key = raw.toLowerCase().replace(/_/g, '-');
  const labels: Record<string, string> = {
    preventive: 'Preventive',
    detective: 'Detective',
    corrective: 'Corrective',
  };
  return labels[key] ?? raw;
}

function formatDomainLabel(domain: unknown): string {
  if (!domain || typeof domain !== 'object') return '';
  const row = domain as { code?: unknown; name?: unknown };
  const code = String(row.code ?? '').trim();
  const name = String(row.name ?? '').trim();
  return [code, name].filter(Boolean).join(' - ');
}

function formatSystemsLabel(systems: unknown): string {
  if (!Array.isArray(systems) || systems.length === 0) return '';
  return systems
    .map((item) => {
      if (!item || typeof item !== 'object') return '';
      const row = item as { name?: unknown; system_id?: unknown };
      const name = String(row.name ?? '').trim();
      const code = String(row.system_id ?? '').trim();
      if (name && code) return `${name} (${code})`;
      return name || code;
    })
    .filter(Boolean)
    .join(', ');
}

function formatKeyReportsLabel(reports: unknown): string {
  if (!Array.isArray(reports) || reports.length === 0) return '';
  return reports
    .map((item) => {
      if (!item || typeof item !== 'object') return '';
      return String((item as { name?: unknown }).name ?? '').trim();
    })
    .filter(Boolean)
    .join(', ');
}

/**
 * Derive pagination when the API omits `total_items` (never treat page length as full dataset size).
 */
export function parseControlsPagination(
  pagination: Record<string, unknown> | undefined,
  listLength: number,
  page: number,
  pageSize: number
): { totalItems: number; totalPages: number; hasExactTotal: boolean } {
  const rawTotal = pagination?.total_items;
  const rawPages = pagination?.total_pages;

  if (Number.isFinite(Number(rawTotal)) && Number(rawTotal) >= 0) {
    const totalItems = Number(rawTotal);
    const totalPages =
      Number.isFinite(Number(rawPages)) && Number(rawPages) >= 1
        ? Number(rawPages)
        : Math.max(1, Math.ceil(totalItems / pageSize));
    return { totalItems, totalPages, hasExactTotal: true };
  }

  const lowerBound = (page - 1) * pageSize + listLength;
  if (listLength < pageSize) {
    return { totalItems: lowerBound, totalPages: Math.max(1, page), hasExactTotal: false };
  }

  return {
    totalItems: lowerBound,
    totalPages: Math.max(page + 1, page),
    hasExactTotal: false,
  };
}

function mapControlRow(control: Record<string, unknown>): ControlListRow {
  const apiStatus = String(control.status ?? 'draft').toLowerCase().trim();
  const effectiveStatus = getControlEffectiveStatus(control);
  const displayId = String(control.control_id || control.control_display_id || '').trim();
  const id = String(control.id ?? '').trim();
  const classificationRaw = String(control.classification ?? 'business').trim().toLowerCase();
  const classification: 'business' | 'itgc' = classificationRaw === 'itgc' ? 'itgc' : 'business';
  const source = String(control.source || control.import_source || '').trim();
  const assertions = Array.isArray(control.assertions)
    ? control.assertions.map((a) => String(a).trim()).filter(Boolean)
    : [];

  const isITGC = classification === 'itgc';
  const domainLabel = isITGC ? formatDomainLabel(control.itgc_domain) : '';
  const systemsLabel = isITGC ? formatSystemsLabel(control.itgc_systems) : '';

  return {
    id,
    displayId,
    controlId: displayId,
    name: String(control.title || control.control_name || ''),
    owner: String(control.owner || 'Unassigned'),
    lastEdited: formatDateCell(
      control.last_edited || control.updated_at || control.lastEdited || control.updatedAt
    ),
    frequency: String(control.frequency || 'Not set'),
    type:
      control.control_type === 'manual'
        ? 'Manual'
        : control.control_type === 'automated'
          ? 'Automated'
          : control.control_type === 'it-dependent-manual'
            ? 'IT Dependant Manual'
            : String(control.control_type || ''),
    soxProcess: String(control.sox_process || ''),
    significance: String(control.significance || ''),
    classification,
    testExecutionCount: Number(control.test_execution_count) || 0,
    riskCount: Number(control.risk_count) || 0,
    apiStatus,
    effectiveStatus,
    importSource: String(control.import_source || source || 'single_upload'),
    clientControlId: String(control.client_control_id || '').trim(),
    controlWording: String(control.control_wording_text || '').trim(),
    objective: String(control.objective || '').trim(),
    controlNature: formatNatureLabel(String(control.control_nature || '')),
    mrcControl: typeof control.mrc_control === 'boolean' ? control.mrc_control : null,
    department: String(control.department || '').trim(),
    controlOwner: String(control.control_owner ?? control.controlOwner ?? '').trim(),
    keyReportsLabel: formatKeyReportsLabel(control.key_reports ?? control.keyReports),
    externalAuditorReliance:
      typeof control.external_auditor_reliance === 'boolean'
        ? control.external_auditor_reliance
        : null,
    assertions,
    source,
    sourceLabel: formatControlSourceLabel(source),
    domainLabel,
    systemsLabel,
    isSeeded: Boolean(control.is_seeded ?? control.isSeeded),
  };
}

export async function fetchControlsListPage(
  listParams: ControlsListParams
): Promise<ControlsListPageResult> {
  const {
    page,
    pageSize,
    search,
    filterType,
    filterStatus,
    filterFrequency,
    filterSignificance,
    filterClassification,
    sortKey,
    sortDir,
    auditYearId,
  } = listParams;

  const userHash = localStorage.getItem('userHash') || '';
  const params: Record<string, string | number> = {
    page,
    page_size: pageSize,
    ...(search.trim() ? { search: search.trim() } : {}),
    ...(filterType !== 'all' ? { control_type: filterType } : {}),
    ...(filterStatus !== 'all' ? { status: filterStatus } : {}),
    ...(filterFrequency !== 'all' ? { frequency: filterFrequency } : {}),
    ...(filterSignificance !== 'all' ? { significance: filterSignificance } : {}),
    ...(filterClassification !== 'all' ? { classification: filterClassification } : {}),
    ...(sortKey ? { sort_by: sortKey, sort_order: sortDir } : {}),
    ...(auditYearId ? { audit_year_id: auditYearId } : {}),
  };

  const response = await axiosInstance.get('/api/sox-controls', {
    headers: { Authorization: userHash },
    params,
  });

  const body = response.data ?? {};
  const root = body.data && typeof body.data === 'object' ? body.data : body;

  const list = Array.isArray(root?.controls)
    ? root.controls
    : Array.isArray(root?.items)
      ? root.items
      : Array.isArray(root)
        ? root
        : [];

  const pagination = (root?.pagination ?? {}) as Record<string, unknown>;
  const { totalItems, totalPages, hasExactTotal } = parseControlsPagination(
    pagination,
    list.length,
    page,
    pageSize
  );

  return {
    rows: list.map((row) => mapControlRow(row as Record<string, unknown>)),
    totalItems,
    totalPages,
    hasExactTotal,
  };
}
